import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Tabs } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/SkillsIndex';
import block from 'propmods';

import { WithSnapshot, withSnapshot } from '../../context/snapshotContext';
import { routes } from '../../lib/routes';
import { commonComments } from '../../locale/ru/operations';
import { Operation, OperationEvent } from '../../model/operation';
import { Channel, Skill, SkillWithLastOperation } from '../../model/skill';
import { showAppStoreUi, showAutoAppUi, showSmartScreenUi } from '../../utils';
import { getLastOperation } from '../../utils/skill';
import CardList from '../CardList/CardList';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import Events from '../Events/Events';
import Footer from '../Footer/Footer';
import { H2 } from '../Typo/Typo';

import './SkillsIndex.scss';

const t = i18n(keyset);

interface SkillsIndexProps {
    onCreateSkillClick: () => void;
}

enum Tab {
    AutoApps = 'autoApps',
    Skills = 'skills',
    TvApps = 'tvApps',
    SmartScreenApps = 'smartScreenApps',
}

const b = block('SkillsIndex');

type Predicate = (params: SkillWithLastOperation) => boolean;
type Transducer = (p: Predicate) => Predicate;
type Reducer = (p1: Predicate, p2: Predicate) => Predicate;

const not: Transducer = (predicate) => (params) => !predicate(params);
const or: Reducer = (p1, p2) => (params) => p1(params) || p2(params);

const needsAttention: Predicate = ({ lastOperation }) =>
    lastOperation !== undefined && ['reviewCancelled', 'reviewRejected', 'deployRejected'].includes(lastOperation.type);
const isDraft: Predicate = ({ skill }) => !skill.onAir;

const SkillsIndex = (props: SkillsIndexProps & WithSnapshot) => {
    const { onCreateSkillClick, snapshot } = props;
    const { operations, skills, maxSkillsPerUser } = snapshot;
    const [activeTab, setActiveTab] = useState<Tab>(Tab.Skills);
    const location = useLocation();
    const history = useHistory();

    const handleTabSelection = useCallback(
        (value: Tab) => {
            switch (value) {
                case Tab.Skills:
                    history.replace(routes.app.skills);
                    break;
                case Tab.AutoApps:
                    history.replace(routes.app.installableApps(Channel.AutoApp));
                    break;
                case Tab.TvApps:
                    history.replace(routes.app.installableApps(Channel.TvApp));
                    break;
                case Tab.SmartScreenApps:
                    history.replace(routes.app.installableApps(Channel.SmartScreenApp));
                    break;
            }
        },
        [history],
    );

    useEffect(() => {
        switch (location.pathname) {
            case routes.app.skills:
                setActiveTab(Tab.Skills);
                break;
        }
    }, [location]);

    const skillsById = skills.reduce(
        (acc, skill) => {
            acc[skill.id] = skill;
            return acc;
        },
        {} as Record<string, Skill>,
    );

    const getOperationName = (operation: Operation) => {
        const skill = operation.itemId ? skillsById[operation.itemId] : undefined;

        if (skill && skill.name) {
            return skill.name;
        }

        if (skill && skill.draft && skill.draft.name) {
            return skill.draft.name;
        }

        return operation?.itemName || t('default-dialog-name');
    };

    const getOperationId = (operation: Operation) => {
        const skill = operation.itemId ? skillsById[operation.itemId] : undefined;

        return skill ? operation.itemId : undefined;
    };

    const getSkillChannel = (operation: Operation) => {
        const skill = operation.itemId ? skillsById[operation.itemId] : undefined;

        return skill?.channel || Channel.AliceSkill;
    };

    const events: OperationEvent<React.ReactNode>[] = operations.slice(0, 5).map((operation) => {
        const channel = getSkillChannel(operation) ?? Channel.AliceSkill;

        return {
            skillEntity: {
                name: getOperationName(operation),
                id: getOperationId(operation),
                channel,
            },
            operation: {
                ...operation,
                comment: operation?.comment || commonComments?.[operation.type] || '',
            },
        };
    });

    const skillsWithLastOperation = skills.map((skill) => ({
        skill,
        lastOperation: getLastOperation(operations, skill.id),
    }));

    const alertCards = skillsWithLastOperation.filter(needsAttention);
    const draftCards = skillsWithLastOperation.filter(isDraft);
    const skillCards = skillsWithLastOperation.filter(not(or(isDraft, needsAttention)));

    const appStoreNavigationTabItems = [
        {
            id: Tab.Skills,
            title: t('alice-skills'),
        },
        {
            id: Tab.TvApps,
            title: t('tv-apps'),
        },
    ];
    if (showAutoAppUi()) {
        appStoreNavigationTabItems.push({
            id: Tab.AutoApps,
            title: t('auto-apps'),
        });
    }
    if (showSmartScreenUi()) {
        appStoreNavigationTabItems.push({
            id: Tab.SmartScreenApps,
            title: t('smart-screen-apps'),
        });
    }

    return (
        <ErrorBoundary withHomepageLink>
            <div {...b('wrapper')}>
                <div {...b()}>
                    <div {...b('content')}>
                        {alertCards.length > 0 && (
                            <section {...b('section')}>
                                <H2>{t('need-attention')}</H2>
                                <CardList skills={alertCards} theme="danger" />
                            </section>
                        )}

                        {showAppStoreUi() && (
                            <section {...b('section')}>
                                <div {...b('switcher')}>
                                    <h2 {...b('H2')}>{t('drafts')}</h2>
                                    <Tabs
                                        activeTab={activeTab}
                                        items={appStoreNavigationTabItems}
                                        onSelectTab={handleTabSelection}
                                        {...b('custom_tabs')}
                                    />
                                </div>
                            </section>
                        )}
                        <section>
                            {!showAppStoreUi() && <H2>{t('drafts')}</H2>}
                            <CardList
                                skills={draftCards}
                                theme="draft"
                                creationCard={{
                                    onClick: onCreateSkillClick,
                                    disabled: skills.length >= maxSkillsPerUser,
                                }}
                            />
                            <p>{t('drafts-description')}</p>
                        </section>

                        {skillCards.length > 0 && (
                            <section {...b('section')}>
                                <H2>{t('published')}</H2>
                                <CardList skills={skillCards} theme="normal" />
                                <p>{t('after-publish')}</p>
                            </section>
                        )}

                        {events.length > 0 && (
                            <section {...b('section')}>
                                <H2>{t('events')}</H2>
                                <Events showHeader showSkill events={events} />
                            </section>
                        )}
                    </div>
                </div>

                <Footer />
            </div>
        </ErrorBoundary>
    );
};

export default withSnapshot(SkillsIndex);
